import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { barsApi } from '@/api/endpoints/bars';
import { adminApi } from '@/api/endpoints/admin';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageSpinner,
  Button,
  Input,
  Badge,
  ButtonGroup,
  ToggleButton,
} from '@/components/ui';
import { FormField } from '@/components/form';
import {
  Sparkles,
  Calendar,
  Wand2,
  CheckCircle,
  Lock,
  Unlock,
  Globe,
  FileText,
  Image,
  ExternalLink,
  Clock,
  Building2,
  Camera,
} from 'lucide-react';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/cn';

const barSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  address: z.string().min(1, 'Address is required'),
  websiteUrl: z.string().url().optional().or(z.literal('')),
  phoneNumber: z.string().optional(),
  servesBrunch: z.boolean(),
  outdoorSeating: z.boolean(),
  allowsDogs: z.boolean(),
  goodForWatchingSports: z.boolean(),
  contentMode: z.enum(['auto', 'manual', 'hybrid']),
  manualFields: z.array(z.string()),
  aiGuidance: z.string().optional(),
  extractionMode: z.enum(['auto', 'manual']),
});

type BarFormData = z.infer<typeof barSchema>;

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getTargetIcon(type: string) {
  switch (type) {
    case 'pdf':
      return <FileText className="h-4 w-4" />;
    case 'image':
      return <Image className="h-4 w-4" />;
    default:
      return <Globe className="h-4 w-4" />;
  }
}

// Field toggle pill component for better visibility
function FieldToggle({
  isManual,
  onToggle,
}: {
  isManual: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors',
        isManual
          ? 'bg-accent-primary/20 text-accent-primary hover:bg-accent-primary/30'
          : 'bg-[var(--color-border)] text-[var(--color-muted)] hover:bg-[var(--color-border)]/80'
      )}
    >
      {isManual ? (
        <>
          <Unlock className="h-3 w-3" />
          Manual
        </>
      ) : (
        <>
          <Lock className="h-3 w-3" />
          AI
        </>
      )}
    </button>
  );
}

export function BarManagePage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['bar-manage', id],
    queryFn: () => barsApi.getManage(id!),
    enabled: !!id,
  });

  const bar = data?.bar;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<BarFormData>({
    resolver: zodResolver(barSchema),
    values: bar
      ? {
          name: bar.name,
          description: bar.description || '',
          address: bar.address,
          websiteUrl: bar.websiteUrl || '',
          phoneNumber: bar.phoneNumber || '',
          servesBrunch: bar.servesBrunch,
          outdoorSeating: bar.outdoorSeating,
          allowsDogs: bar.allowsDogs,
          goodForWatchingSports: bar.goodForWatchingSports,
          contentMode: bar.contentMode || 'auto',
          manualFields: bar.manualFields || [],
          aiGuidance: bar.aiGuidance || '',
          // Combine specials/events mode into single extraction mode
          extractionMode: bar.specialsMode === 'manual' ? 'manual' : 'auto',
        }
      : undefined,
  });

  const contentMode = watch('contentMode');
  const manualFields = watch('manualFields');
  const extractionMode = watch('extractionMode');

  const isFieldManual = (field: string) =>
    contentMode === 'manual' || (contentMode === 'hybrid' && manualFields.includes(field));

  // Field is disabled if in auto mode OR in hybrid mode and not manually toggled
  const isFieldDisabled = (field: string) =>
    contentMode === 'auto' || (contentMode === 'hybrid' && !manualFields.includes(field));

  const toggleManualField = (field: string) => {
    if (contentMode !== 'hybrid') return;
    const current = manualFields || [];
    if (current.includes(field)) {
      setValue('manualFields', current.filter((f) => f !== field), { shouldDirty: true });
    } else {
      setValue('manualFields', [...current, field], { shouldDirty: true });
    }
  };

  const updateMutation = useMutation({
    mutationFn: (data: BarFormData) => {
      // Convert extractionMode to specialsMode/eventsMode for backend
      const { extractionMode: mode, ...rest } = data;
      return adminApi.updateBar(id!, {
        ...rest,
        specialsMode: mode,
        eventsMode: mode,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bar-manage', id] });
      toast.success('Bar updated successfully');
    },
    onError: () => {
      toast.error('Failed to update bar');
    },
  });

  const extractMutation = useMutation({
    // Deliberate per-bar action → force (bypass the content-hash cache) so it
    // always re-extracts rather than silently no-op'ing on a cache hit.
    mutationFn: () => adminApi.triggerExtraction({ barId: id, force: true }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bar-manage', id] });
      toast.success(`Extraction complete: ${data.message || 'Done'}`);
    },
    onError: () => {
      toast.error('Extraction failed');
    },
  });

  const crawlMutation = useMutation({
    mutationFn: () => adminApi.triggerCrawl({ barId: id }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bar-manage', id] });
      toast.success(`Crawl complete: ${data.message || 'Done'}`);
    },
    onError: () => {
      toast.error('Crawling failed');
    },
  });

  // Logo upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => adminApi.uploadBarLogo(id!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bar-manage', id] });
      setLogoPreview(null);
      toast.success('Logo uploaded successfully');
    },
    onError: () => {
      toast.error('Failed to upload logo');
      setLogoPreview(null);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please select a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (1MB)
    if (file.size > 1024 * 1024) {
      toast.error('Image must be less than 1MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);

    // Upload
    uploadLogoMutation.mutate(file);
  };

  if (isLoading) return <PageSpinner />;

  if (!bar) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--color-muted)]">Bar not found</p>
      </div>
    );
  }

  const parseConfig = bar.parseConfig;
  const hasTargets = parseConfig?.targets && parseConfig.targets.length > 0;

  // Disabled field styling
  const disabledClass = 'bg-[var(--color-border)]/30 cursor-not-allowed opacity-60';

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header with Logo */}
      <div className="flex items-start gap-4">
        {/* Logo with upload capability */}
        <div className="relative shrink-0">
          {logoPreview || bar.logoUrl ? (
            <img
              src={logoPreview || bar.logoUrl!}
              alt={bar.name}
              className={cn(
                'h-16 w-16 rounded-lg object-cover',
                uploadLogoMutation.isPending && 'opacity-50'
              )}
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-[var(--color-border)]">
              <Building2 className="h-8 w-8 text-[var(--color-muted)]" />
            </div>
          )}
          {/* Upload overlay button */}
          {isAdmin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadLogoMutation.isPending}
                className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-accent-primary text-white shadow-md transition-transform hover:scale-110 disabled:opacity-50"
                title="Upload new logo"
              >
                {uploadLogoMutation.isPending ? (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <Camera className="h-3 w-3" />
                )}
              </button>
            </>
          )}
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold">{bar.name}</h1>
          <p className="mt-1 truncate text-[var(--color-muted)]">{bar.address}</p>
        </div>

        {/* Action buttons - hidden on mobile, shown on desktop */}
        <div className="hidden shrink-0 gap-2 sm:flex">
          <Link to={`/admin/bars/${id}/specials`}>
            <Button variant="ghost" size="sm">
              <Sparkles className="mr-1.5 h-4 w-4" />
              Specials
            </Button>
          </Link>
          <Link to={`/admin/bars/${id}/events`}>
            <Button variant="ghost" size="sm">
              <Calendar className="mr-1.5 h-4 w-4" />
              Events
            </Button>
          </Link>
        </div>
      </div>

      {/* Mobile action buttons */}
      <div className="flex gap-2 sm:hidden">
        <Link to={`/admin/bars/${id}/specials`} className="flex-1">
          <Button variant="ghost" size="sm" className="w-full">
            <Sparkles className="mr-1.5 h-4 w-4" />
            Specials
          </Button>
        </Link>
        <Link to={`/admin/bars/${id}/events`} className="flex-1">
          <Button variant="ghost" size="sm" className="w-full">
            <Calendar className="mr-1.5 h-4 w-4" />
            Events
          </Button>
        </Link>
      </div>

      {/* Content Management (combined: mode + AI guidance + extraction toggle) */}
      <Card>
        <CardHeader>
          <CardTitle>Content Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Mode selector */}
          <Controller
            name="contentMode"
            control={control}
            render={({ field }) => (
              <ButtonGroup>
                <ToggleButton
                  isActive={field.value === 'auto'}
                  onClick={() => field.onChange('auto')}
                >
                  <Wand2 className="h-4 w-4" />
                  Auto (AI)
                </ToggleButton>
                <ToggleButton
                  isActive={field.value === 'manual'}
                  onClick={() => field.onChange('manual')}
                >
                  <Lock className="h-4 w-4" />
                  Manual
                </ToggleButton>
                <ToggleButton
                  isActive={field.value === 'hybrid'}
                  onClick={() => field.onChange('hybrid')}
                >
                  <Unlock className="h-4 w-4" />
                  Hybrid
                </ToggleButton>
              </ButtonGroup>
            )}
          />

          <p className="text-sm text-[var(--color-muted)]">
            {contentMode === 'auto' && (
              <>
                <strong>Auto:</strong> AI manages all content. Fields are read-only.
              </>
            )}
            {contentMode === 'manual' && (
              <>
                <strong>Manual:</strong> You manage all content. AI extraction disabled.
              </>
            )}
            {contentMode === 'hybrid' && (
              <>
                <strong>Hybrid:</strong> AI fills in by default. Toggle fields you want to control manually.
              </>
            )}
          </p>

          {/* AI Guidance - shown when not manual */}
          {contentMode !== 'manual' && (
            <div className="border-t border-[var(--color-border)] pt-4">
              <label className="mb-2 block text-sm font-medium">AI Guidance (optional)</label>
              <p className="mb-2 text-xs text-[var(--color-muted)]">
                Help the AI understand your bar better. Use this to correct mistakes, clarify confusing info, or provide context the AI might miss.
              </p>
              <textarea
                className="input min-h-[80px] w-full resize-y text-sm"
                placeholder="Examples:&#10;• Happy hour is 3-6pm, not 4-7pm as shown on the website&#10;• We're the downtown location, not the one in Midtown&#10;• Tuesday trivia starts at 7pm, not 8pm&#10;• Ignore the 'coming soon' specials section"
                maxLength={500}
                {...register('aiGuidance')}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                {500 - (watch('aiGuidance')?.length || 0)} characters remaining
              </p>
            </div>
          )}

          {/* Specials & Events toggle - only shown in Hybrid mode */}
          {contentMode === 'hybrid' && (
            <div className="border-t border-[var(--color-border)] pt-4">
              <p className="mb-2 text-sm font-medium">Specials & Events</p>
              <Controller
                name="extractionMode"
                control={control}
                render={({ field }) => (
                  <ButtonGroup>
                    <ToggleButton
                      isActive={field.value === 'auto'}
                      onClick={() => field.onChange('auto')}
                    >
                      <Wand2 className="h-3 w-3" />
                      AI Extracts
                    </ToggleButton>
                    <ToggleButton
                      isActive={field.value === 'manual'}
                      onClick={() => field.onChange('manual')}
                    >
                      <Lock className="h-3 w-3" />
                      I Manage
                    </ToggleButton>
                  </ButtonGroup>
                )}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                {extractionMode === 'auto'
                  ? 'AI automatically extracts specials and events from your website'
                  : 'You manually manage all specials and events'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pages Being Monitored (Parse Config) */}
      {contentMode !== 'manual' && hasTargets && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Pages Being Monitored
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[var(--color-muted)]">
              AI extracts specials and events from these pages.
            </p>
            <div className="divide-y divide-[var(--color-border)] rounded-lg border border-[var(--color-border)]">
              {parseConfig?.targets?.map((target, i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-[var(--color-border)]">
                    {getTargetIcon(target.type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <a
                      href={target.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-accent-primary hover:underline"
                    >
                      <span className="truncate">{target.url}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                    {target.selector && (
                      <code className="mt-0.5 block truncate text-xs text-[var(--color-muted)]">
                        {target.selector}
                      </code>
                    )}
                  </div>
                  <Badge variant="secondary" className="shrink-0">
                    {target.type}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Crawling Status */}
      {contentMode !== 'manual' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Website Crawling
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[var(--color-muted)]">
              We scan your website to find basic info (hours, address) and discover pages that might contain specials or events menus.
            </p>
            <div className="flex items-center gap-1.5 text-sm">
              <Clock className="h-4 w-4 text-[var(--color-muted)]" />
              <span className="text-[var(--color-muted)]">Last crawled:</span>
              <span className="font-medium">{formatDate(bar.lastCrawledAt)}</span>
            </div>
            {isAdmin && (
              <Button
                variant="secondary"
                onClick={() => crawlMutation.mutate()}
                isLoading={crawlMutation.isPending}
                className="mt-2"
              >
                Run Crawling Now
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Extraction Status & Run Button */}
      {contentMode !== 'manual' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Specials & Events Extraction
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[var(--color-muted)]">
              We check the pages above for new specials and events. When something changes, we update your listing automatically.
            </p>
            <div className="flex items-center gap-1.5 text-sm">
              <Wand2 className="h-4 w-4 text-[var(--color-muted)]" />
              <span className="text-[var(--color-muted)]">Last extracted:</span>
              <span className="font-medium">{formatDate(bar.lastExtractedAt)}</span>
            </div>
            {isAdmin && (
              <Button
                onClick={() => extractMutation.mutate()}
                isLoading={extractMutation.isPending}
                className="mt-2"
              >
                Run Extraction Now
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bar Details Form */}
      <Card>
        <CardHeader>
          <CardTitle>Bar Details</CardTitle>
          {contentMode === 'auto' && (
            <p className="text-sm text-[var(--color-muted)]">
              Fields are read-only in Auto mode. Switch to Manual or Hybrid to edit.
            </p>
          )}
          {contentMode === 'hybrid' && (
            <p className="text-sm text-[var(--color-muted)]">
              Click the toggle next to a field to switch between AI and Manual control.
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit((data) => updateMutation.mutate(data))}
            className="space-y-4"
          >
            {/* Name and Address */}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label={
                  <span className="flex items-center gap-2">
                    Name
                    {contentMode === 'hybrid' && (
                      <FieldToggle
                        isManual={isFieldManual('name')}
                        onToggle={() => toggleManualField('name')}
                      />
                    )}
                  </span>
                }
                htmlFor="name"
                error={errors.name?.message}
                required
              >
                <Input
                  id="name"
                  {...register('name')}
                  error={!!errors.name}
                  disabled={isFieldDisabled('name')}
                  className={isFieldDisabled('name') ? disabledClass : ''}
                />
              </FormField>

              <FormField
                label={
                  <span className="flex items-center gap-2">
                    Address
                    {contentMode === 'hybrid' && (
                      <FieldToggle
                        isManual={isFieldManual('address')}
                        onToggle={() => toggleManualField('address')}
                      />
                    )}
                  </span>
                }
                htmlFor="address"
                error={errors.address?.message}
                required
              >
                <Input
                  id="address"
                  {...register('address')}
                  error={!!errors.address}
                  disabled={isFieldDisabled('address')}
                  className={isFieldDisabled('address') ? disabledClass : ''}
                />
              </FormField>
            </div>

            {/* Toggleable fields */}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label={
                  <span className="flex items-center gap-2">
                    Phone Number
                    {contentMode === 'hybrid' && (
                      <FieldToggle
                        isManual={isFieldManual('phoneNumber')}
                        onToggle={() => toggleManualField('phoneNumber')}
                      />
                    )}
                  </span>
                }
                htmlFor="phoneNumber"
                error={errors.phoneNumber?.message}
              >
                <Input
                  id="phoneNumber"
                  {...register('phoneNumber')}
                  disabled={isFieldDisabled('phoneNumber')}
                  className={isFieldDisabled('phoneNumber') ? disabledClass : ''}
                />
              </FormField>

              <FormField
                label={
                  <span className="flex items-center gap-2">
                    Website URL
                    {contentMode === 'hybrid' && (
                      <FieldToggle
                        isManual={isFieldManual('websiteUrl')}
                        onToggle={() => toggleManualField('websiteUrl')}
                      />
                    )}
                  </span>
                }
                htmlFor="websiteUrl"
                error={errors.websiteUrl?.message}
              >
                <Input
                  id="websiteUrl"
                  type="url"
                  placeholder="https://"
                  {...register('websiteUrl')}
                  disabled={isFieldDisabled('websiteUrl')}
                  className={isFieldDisabled('websiteUrl') ? disabledClass : ''}
                />
              </FormField>
            </div>

            <FormField
              label={
                <span className="flex items-center gap-2">
                  Description
                  {contentMode === 'hybrid' && (
                    <FieldToggle
                      isManual={isFieldManual('description')}
                      onToggle={() => toggleManualField('description')}
                    />
                  )}
                </span>
              }
              htmlFor="description"
            >
              <textarea
                id="description"
                className={cn('input min-h-[100px] resize-y', isFieldDisabled('description') && disabledClass)}
                {...register('description')}
                disabled={isFieldDisabled('description')}
              />
            </FormField>

            {/* Amenities */}
            <div>
              <p className="mb-3 text-sm font-medium">Amenities</p>
              <div className="flex flex-wrap gap-4">
                {[
                  { key: 'servesBrunch', label: 'Serves Brunch' },
                  { key: 'outdoorSeating', label: 'Outdoor Seating' },
                  { key: 'allowsDogs', label: 'Allows Dogs' },
                  { key: 'goodForWatchingSports', label: 'Good for Sports' },
                ].map(({ key, label }) => (
                  <label
                    key={key}
                    className={cn(
                      'flex items-center gap-2',
                      isFieldDisabled(key) && 'opacity-60 cursor-not-allowed'
                    )}
                  >
                    <input
                      type="checkbox"
                      {...register(key as keyof BarFormData)}
                      disabled={isFieldDisabled(key)}
                      className="rounded"
                    />
                    <span className="text-sm">{label}</span>
                    {contentMode === 'hybrid' && (
                      <FieldToggle
                        isManual={isFieldManual(key)}
                        onToggle={() => toggleManualField(key)}
                      />
                    )}
                  </label>
                ))}
              </div>
            </div>

          </form>
        </CardContent>
      </Card>

      {/* Floating Save Button - only visible when there are unsaved changes */}
      {isDirty && (
        <div className="fixed bottom-6 right-6 z-50 lg:right-10">
          <Button
            onClick={handleSubmit((data) => updateMutation.mutate(data))}
            isLoading={updateMutation.isPending}
            className="shadow-lg"
          >
            <CheckCircle className="mr-2 h-4 w-4" />
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}
